import { useEffect, useRef, useState } from 'react';
import { Capture } from './Capture';
import { Icon } from './Icon';

/**
 * Capture, behind a `+`.
 *
 * The compact capture used to sit open in the header on every screen. Phase 66 gave it a facet
 * row — goal, labels, priority — which is worth its space on the Tasks screen, where you are
 * already thinking about tasks, and not worth a permanent second line of header everywhere else.
 *
 * So the header shows one 32px button. `c` still reaches capture from anywhere; it now opens the
 * box before focusing it, which is the same keystroke doing the same thing with one more step
 * that happens too fast to perceive. The listener lives here rather than in `Capture` because the
 * input does not exist until this component decides it does.
 */
export function CaptureDisclosure() {
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') return setOpen(false);
      if (event.key !== 'c' || event.metaKey || event.ctrlKey || event.altKey) return;
      // The house typing guard: never steal a letter from somebody filling in a field.
      const el = document.activeElement;
      if (
        el instanceof HTMLInputElement ||
        el instanceof HTMLTextAreaElement ||
        el instanceof HTMLSelectElement ||
        (el instanceof HTMLElement && el.isContentEditable)
      ) {
        return;
      }
      event.preventDefault();
      setOpen(true);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  // Only closes on a click outside while it is open, and never while something inside has focus —
  // the goal and label pickers render their popups outside this subtree.
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (wrapper.current?.contains(target)) return;
      if (document.querySelector('.sel-pop:not([hidden])')?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  return (
    <div className="capture-disclosure" ref={wrapper}>
      <button
        type="button"
        className="icon-button"
        aria-expanded={open}
        /*
          "New task", not "Capture" — the submit button inside the panel is already called
          Capture, and two controls with one name in the same subtree is ambiguous to a screen
          reader and to every test that reaches for it by name.
        */
        aria-label="New task"
        aria-keyshortcuts="c"
        title="New task — c"
        onClick={() => setOpen((v) => !v)}
      >
        <Icon name="plus" />
      </button>
      {open && (
        <div className="capture-panel card">
          <Capture compact autoFocusInput />
        </div>
      )}
    </div>
  );
}
